import type { IdentifiedPart, MatchedPart, StagedPartType, StagingBomNode } from './staging-types'

// ADR-0136 — the UID-254 "parked" disposition on this module was RESOLVED
// (re-homed, C1) as an ADDITIVE fourth section. ADR-0142 SUPERSEDES that
// placement: on a BOM session `buildUnifiedBomRows` (below) is the entry
// point `StagingPage` calls, REPLACING the Identified/Matched buckets rather
// than sitting alongside them. `buildBomTree` is no longer sufficient on its
// own — walking `bomHierarchy` alone can silently drop a part the hierarchy
// doesn't reference (ADR-0086 BUG-1, re-arriving via the data-flow direction
// once the buckets are gone) — so `buildUnifiedBomRows` re-bases population
// on `identified[] ∪ matched[]` and uses `bomHierarchy` for ordering/grouping
// only. See ADR-0142 D2. `partitionReadyToAdd`/`ReadyToAddPartition` — the
// OTHER UID-254 disposition, C2 (sunset) — stays deleted; ADR-0142 D2 REOPENS
// the *requirement* that function once encoded (no dropped part), not the
// function itself.

/**
 * ADR-0086 FE normalization: walk the wire `StagingBomNode[]` tree and
 * resolve each `kind: 'part'` leaf's `partId` against a `Map<partId, part>`
 * built from the flat `identified[]`/`matched[]` arrays (the single source
 * of truth for a part's payload — name, type, catalog match, drawing,
 * configuration status, qty). This module produces the FEATURE VIEW MODEL;
 * components bind only to `BomTreeNode`, never to the raw wire
 * `StagingBomNode` shape directly.
 */

interface BomTreeLeafBase {
  readonly kind: 'part'
  readonly nodeId: string
  /** ADR-0136 §4 — `StagingBomNode.name` is captured for every wire node
   * unconditionally (`staging-types.ts`'s `StagingBomNode.name`); this was
   * previously dropped by `walk()`, forcing `partKind: null` "unresolved"
   * rows to fall back to `partId ?? nodeId` for their display text (a raw
   * id, not a name). Carried through here so the leaf's own name is never
   * lost regardless of whether `partId` resolves against `identified[]`/
   * `matched[]`. */
  readonly name: string
  readonly bomLine: number | null
  /** The CAPTURED per-node quantity (`StagingBomNode.quantity`) — the
   * customer's own verbatim assertion, immutable. See `desiredQuantity`
   * below for the editable working value. */
  readonly quantity: number | null
  /** ADR-0156 D1/D5(4) — the WORKING quantity a human has typed for this BOM
   * line (`StagingBomNode.desiredQuantity`). `null` = never edited. `null`
   * for every `ungrouped` coverage-diff leaf (ADR-0149 Am.1 D17 reasoning:
   * no wire node backs it, so there is no edit to carry) and for an
   * `identified` leaf (never read — D1.1). */
  readonly desiredQuantity: number | null
  readonly partId: string | null
  /** ADR-0149 Am.1 D17/D19 — the wire node's OWN `statedPartType`, carried
   * through for the SAME reason `name` is (ADR-0136 §4, comment above): an
   * unresolved leaf's own facts must not be lost because `partId` did not
   * resolve. `null` for a node that never had one (including every
   * synthesized `ungrouped` leaf below, which has no wire node behind it at
   * all — see {@link buildUnifiedBomRows}). Rendering/precedence rules for
   * this value are `StagingBomLeafRow`'s concern (Am.1 D19), not this
   * module's — a RESOLVED leaf's own `part.type` still wins (D19(1)). */
  readonly statedPartType: StagedPartType | null
  /** ADR-0149 Am.1 D17/D19 — the wire node's OWN `statedMaterial`, verbatim.
   * Same carry-through reasoning as `statedPartType` above. NOT
   * `IdentifiedPart.material` (different provenance/lifecycle — see
   * `staging-types.ts`'s `StagingBomNode.statedMaterial` doc comment). */
  readonly statedMaterial: string | null
  /** UID-517 item 4 — a REAL, cross-session Parts Library thumbnail reused
   * for a still-unresolved leaf, carried through unconditionally from the
   * wire node (same terms as `statedPartType`/`statedMaterial` above).
   * `null` on every RESOLVED leaf (an `identified`/`matched` leaf renders
   * its OWN `part.thumbnailUrl` instead — see `StagingBomLeafRow`) and on
   * every synthesized `ungrouped` leaf (no wire node backs it). */
  readonly crossSessionThumbnailUrl: string | null
  /** UID-536 (T3) — the wire node's OWN `statedProcess`, verbatim. Same
   * carry-through reasoning as `statedMaterial` above (`statedMaterial`'s
   * DIRECT SIBLING — NOT `StagedConfiguration.process`, a different fact
   * with different provenance/lifecycle). `null` for a node that never had
   * one, including every synthesized `ungrouped` leaf below. */
  readonly statedProcess: string | null
  /** UX-spec §9.7/§9.8.3 — how many `bomHierarchy` leaves cited this SAME
   * `partId` before dedupe collapsed them to this one surviving row. `1` for
   * every leaf that was never a dedupe target: a leaf with no duplicate, an
   * unresolved leaf (never deduped, D17.1), and every `ungrouped`
   * coverage-diff leaf (not referenced by any tree leaf at all, so
   * multiplicity does not apply — stays `1`, never absent, so callers don't
   * need an `undefined` branch). Stamped by {@link dedupeByPartId} on the
   * one resolved leaf it keeps. */
  readonly occurrenceCount: number
  /**
   * Gate-5 re-review residual — this leaf's own 0-based position among its
   * DIRECT siblings (the array it was walked from: `bomHierarchy` at the
   * top level, or an assembly's `children`). Unlike `bomLine` (customer-
   * supplied, `null` whenever the sheet has no recognized line-number
   * column) this is structural — every leaf has one, by construction of the
   * array it came from — so `StagingBomLeafRow`'s unresolved arm can fall
   * back to it (as `ordinal = siblingIndex + 1`) to keep the Qty-input/
   * checkbox accessible name unique when `bomLine` is `null` on every leaf.
   * Never rendered on its own and never a substitute for `bomLine` when
   * `bomLine` IS present — `bomLine` is the customer's own reference and
   * stays preferred (see `StagingBomLeafRow`'s `bomLineLabel`).
   */
  readonly siblingIndex: number
  /** ADR-0157 D3 — carried unconditionally from the wire node, same
   * carry-through reasoning as `statedPartType`/`statedMaterial` above.
   * `null` for every resolved leaf and every synthesized `ungrouped` leaf
   * (neither has a dismissal concept — D3 scopes soft-dismiss to the
   * unresolved arm only). */
  readonly dismissedAt: string | null
  /** UID-497 (T10) — the material a HUMAN CHOSE on this BOM line, or `null`
   * when they never have. DISTINCT from `statedMaterial` above (the customer's
   * own CSV cell, verbatim): the row renders the chosen one when set and the
   * stated one otherwise, and the two are never reconciled. */
  readonly selectedMaterial: string | null
  /** UID-497 (T10) — the drawing attached to THIS BOM line, or `null` for none
   * (the `drawMissing` "needs attention" state on a Make part). */
  readonly drawingFilename: string | null
}

/**
 * A discriminated union on `partKind` (not a plain `part: A | B | null`
 * field) so a `node.partKind === 'identified'` check narrows `node.part` to
 * `IdentifiedPart` at the call site — needed by `StagingBomPartsTree` to
 * pass a well-typed `part` into `StagingBomLeafRow`'s `kind`-discriminated
 * props without an `as` cast.
 */
export type BomTreeLeafNode =
  | (BomTreeLeafBase & { readonly partKind: 'identified'; readonly part: IdentifiedPart })
  | (BomTreeLeafBase & { readonly partKind: 'matched'; readonly part: MatchedPart })
  | (BomTreeLeafBase & {
      readonly partKind: null
      /** `null` when `partId` is null/absent or doesn't resolve against
       * either flat array (tolerated, not thrown). */
      readonly part: null
    })

export interface BomTreeAssemblyNode {
  readonly kind: 'assembly'
  readonly nodeId: string
  readonly name: string
  readonly revision: string | null
  /** ADR-0086 D4 — semantics undefined; null means "omit the '· N builds'
   * segment" (D4), never a fabricated value. */
  readonly buildCount: number | null
  readonly quantity: number | null
  readonly children: readonly BomTreeNode[]
}

export type BomTreeNode = BomTreeAssemblyNode | BomTreeLeafNode

function walk(
  node: StagingBomNode,
  identifiedMap: ReadonlyMap<string, IdentifiedPart>,
  matchedMap: ReadonlyMap<string, MatchedPart>,
  siblingIndex: number,
): BomTreeNode {
  if (node.kind === 'assembly') {
    return {
      kind: 'assembly',
      nodeId: node.nodeId,
      name: node.name,
      revision: node.revision ?? null,
      buildCount: node.buildCount ?? null,
      quantity: node.quantity ?? null,
      children: node.children.map((child, index) => walk(child, identifiedMap, matchedMap, index)),
    }
  }

  const partId = node.partId ?? null
  const identifiedPart = partId ? identifiedMap.get(partId) : undefined
  const matchedPart = partId ? matchedMap.get(partId) : undefined
  const base = {
    kind: 'part' as const,
    nodeId: node.nodeId,
    name: node.name,
    bomLine: node.bomLine ?? null,
    quantity: node.quantity ?? null,
    desiredQuantity: node.desiredQuantity ?? null,
    partId,
    // ADR-0149 Am.1 D17 — carried from the wire node unconditionally, same
    // as `name` above; NOT gated on whether `partId` resolved.
    statedPartType: node.statedPartType ?? null,
    statedMaterial: node.statedMaterial ?? null,
    // UID-517 item 4 — carried through unconditionally, same as the two
    // stated fields above; meaningless on a resolved leaf (that arm reads
    // `part.thumbnailUrl` instead) but harmless to carry regardless.
    crossSessionThumbnailUrl: node.crossSessionThumbnailUrl ?? null,
    statedProcess: node.statedProcess ?? null,
    // Default; overwritten by `dedupeByPartId` on the one surviving resolved
    // leaf when its `partId` occurs more than once (UX-spec §9.7/§9.8.3).
    occurrenceCount: 1,
    siblingIndex,
    // ADR-0157 D3 — carried through unconditionally, same as
    // `statedPartType`/`statedMaterial` above; meaningless on a resolved
    // leaf (1017's CHECK forbids it on an assembly, and D3 never writes it
    // on identified/matched), but harmless to carry regardless.
    dismissedAt: node.dismissedAt ?? null,
    // UID-497 (T10) — the human's own choices on this line, carried on the
    // same unconditional terms as the stated values above: the row decides
    // whether to offer a control, the view model just tells the truth about
    // what is stored.
    selectedMaterial: node.selectedMaterial ?? null,
    drawingFilename: node.drawingFilename ?? null,
  }

  if (identifiedPart) return { ...base, partKind: 'identified', part: identifiedPart }
  if (matchedPart) return { ...base, partKind: 'matched', part: matchedPart }
  return { ...base, partKind: null, part: null }
}

/**
 * Normalize the wire `bomHierarchy` + flat `identified[]`/`matched[]` arrays
 * into the tree view model. `identified`/`matched` are the single source of
 * truth for a leaf's payload — this function never duplicates or mutates
 * them, only resolves references by `partId`.
 */
export function buildBomTree(
  bomHierarchy: readonly StagingBomNode[],
  identified: readonly IdentifiedPart[],
  matched: readonly MatchedPart[],
): readonly BomTreeNode[] {
  const identifiedMap = new Map(identified.map((part) => [part.partId, part] as const))
  const matchedMap = new Map(matched.map((part) => [part.partId, part] as const))
  return bomHierarchy.map((node, index) => walk(node, identifiedMap, matchedMap, index))
}

export interface BomTreeLeafRef {
  readonly partId: string
  readonly partKind: 'identified' | 'matched'
}

/**
 * Every resolved descendant leaf under a node, each carrying which flat array
 * (`identified[]`/`matched[]`) its `partId` resolved against — needed so a
 * group/header checkbox toggle can route each leaf through the right
 * `onToggleSelectIdentified`/`onToggleSelectMatched` callback without guessing
 * from current selection-set membership (which is ambiguous for a
 * not-yet-selected leaf). Skips leaves with no `partId` and leaves whose
 * `partId` doesn't resolve against either flat array (`partKind === null`) —
 * neither is a selectable unit.
 *
 * W3 correction (Gate-5) — this used to claim to be "the single leaf walk"
 * that every other leaf-derived value is projected from "so they can't drift
 * apart". That claim is now FALSE and must not be restored: ADR-0142 D3's
 * predicate-separation rule REQUIRES {@link countRenderableRows} (renders a
 * row, including unresolved leaves) and {@link collectSelectablePartIds}
 * (selectable AND not an ignored match) to stay independent walks with their
 * own predicates, not re-derived from this one. There are three walks by
 * design, not by drift, and unifying them would silently reintroduce exactly
 * the dropped-row/over-selection bugs D3 exists to prevent.
 */
export function collectLeafRefs(node: BomTreeNode): readonly BomTreeLeafRef[] {
  if (node.kind === 'part') {
    return node.partId && node.partKind ? [{ partId: node.partId, partKind: node.partKind }] : []
  }
  return node.children.flatMap((child) => collectLeafRefs(child))
}

/**
 * The `partId`s of every resolved descendant leaf — {@link collectLeafRefs}
 * projected to just the id. Used to derive an assembly-group checkbox's
 * roll-up state (§3.5) and the referenced-leaf set that splits ready-to-add
 * parts into "BOM parts" vs. "Ungrouped parts"; a single `Set<partId>` of
 * selected leaves is the only state that needs to exist, and every
 * group/header checkbox's checked/indeterminate value is *derived*.
 */
export function collectLeafPartIds(node: BomTreeNode): readonly string[] {
  return collectLeafRefs(node).map((ref) => ref.partId)
}

/**
 * ADR-0142 D3 / UX-spec §5.2 — the "renders a row" predicate used for
 * STRUCTURAL empty-assembly pruning, deliberately SEPARATE from
 * {@link collectLeafRefs}. `collectLeafRefs` excludes `partKind: null`
 * (unresolved) leaves because they are not a *selectable* unit — but they DO
 * render a row (`StagingBomLeafRow kind="unresolved"`). Reusing the
 * selection predicate here would silently prune an assembly that contains
 * only unresolved BOM lines, which is a fresh dropped-row bug, not a
 * cleanup. Every `kind: 'part'` leaf counts, resolved or not.
 */
export function countRenderableRows(node: BomTreeNode): number {
  if (node.kind === 'part') return 1
  return node.children.reduce((sum, child) => sum + countRenderableRows(child), 0)
}

/**
 * ADR-0142 D3(iii) / UX-spec §5.2 — bottom-up STRUCTURAL pruning of any
 * `BomTreeAssemblyNode` whose recursive renderable-row count
 * ({@link countRenderableRows}) is zero. This is NOT the prototype's
 * filter-driven header pruning (`dc.html:9494-9501`, ported nowhere — that
 * logic sits downstream of a search/status filter ADR-0124 D6 deleted, so it
 * would be a dead, provably-no-op seam here). A structurally empty assembly
 * (a captured BOM node with nothing beneath it, or — after recursion — an
 * assembly whose only children are themselves-empty sub-assemblies) is real
 * data, not a filter artefact, and removing its HEADER never narrows the
 * rendered PART population, so it cannot breach ADR-0124 D6.
 */
export function pruneEmptyAssemblies(nodes: readonly BomTreeNode[]): readonly BomTreeNode[] {
  const result: BomTreeNode[] = []
  for (const node of nodes) {
    if (node.kind === 'part') {
      result.push(node)
      continue
    }
    const prunedChildren = pruneEmptyAssemblies(node.children)
    const rowCount = prunedChildren.reduce((sum, child) => sum + countRenderableRows(child), 0)
    if (rowCount === 0) continue
    result.push({ ...node, children: prunedChildren })
  }
  return result
}

/**
 * ADR-0142 D2 / UX-spec §2 — the section's count pill is the leaf PART-ROW
 * count (grouped + ungrouped), never the top-level NODE count
 * (`bomTree.length` counts assemblies, not parts — the bug D3/D12 corrects).
 * Counts every `kind: 'part'` row, resolved or not, matching
 * {@link countRenderableRows}'s predicate (a distinct name is kept because
 * the two call sites answer different questions — "does this section have
 * anything to show" vs. "how many part rows are in it" — even though today
 * they compute the same thing over the same tree shape).
 */
export function countPartRows(nodes: readonly BomTreeNode[]): number {
  return nodes.reduce((sum, node) => sum + countRenderableRows(node), 0)
}

/**
 * ADR-0142 D7 (2) — a broader-than-{@link collectLeafRefs} selectable-id
 * walk used to re-derive `StagingPage`'s `selectableRowIds` in the unified
 * list's own render order. Differs from `collectLeafRefs` in exactly one
 * way: a `matched` leaf whose `part.status === 'ignored'` is excluded, the
 * same "an ignored match's checkbox is disabled" rule the flat
 * `matchedSelectableIds` filter already applies — carried here so shift-click
 * range selection over the unified list can't select a disabled row.
 */
export function collectSelectablePartIds(node: BomTreeNode): readonly string[] {
  if (node.kind === 'part') {
    if (!node.partId || !node.partKind) return []
    if (node.partKind === 'matched' && node.part.status === 'ignored') return []
    return [node.partId]
  }
  return node.children.flatMap((child) => collectSelectablePartIds(child))
}

/**
 * ADR-0142 D13/D14.1 (Gate-5 C2), RETRACTED-AND-CORRECTED by Amendment 2
 * D16/D17/D17.1 (Gate-5 C3) — "exactly once" is once per PART (`partId`),
 * never once per BOM POSITION, and dedupe's DOMAIN is the RESOLVED
 * population only. `partId` is a staged-FILE identity
 * (`staging-store.ts:255`, `:285`) — the parser applies no uniqueness rule to
 * it (`bom-spreadsheet-parser.ts:385`, `:463`) and a real BOM legitimately
 * uses the same file in two assemblies. Keeps the FIRST occurrence in the
 * tree's own document order (depth-first, sibling order — ADR-0142 D15's
 * capture order, never sorted) and drops every later leaf whose `partId` was
 * already seen — but ONLY among RESOLVED leaves (`partKind !== null`).
 *
 * D16 retracted the premise D14.1(1) rested on: `partKind: null`
 * (unresolved) leaves do NOT "carry no `partId`" — `walk()` builds `base`
 * with `partId` unconditionally (`:91`, `:100`) and every return branch,
 * including the unresolved one, spreads it (`:103-105`). The correct reason
 * an unresolved leaf is NEVER deduped is semantic, not mechanical (D17(1)):
 * such a leaf's `partId` is, by construction, in NEITHER `identified[]` nor
 * `matched[]` (`walk()` fell through both map lookups), so it is outside the
 * domain of the "exactly once per staged file" invariant dedupe enforces —
 * running the enforcement mechanism over rows the invariant does not govern
 * is unlicensed and silently drops a row with no recovery path (D16). Each
 * unresolved leaf is a distinct BOM line with no part behind it, and
 * collapsing two of them would be a fresh dropped-row bug of exactly the
 * class D3's predicate separation exists to prevent.
 *
 * MUST run BEFORE {@link pruneEmptyAssemblies} (D14.1(2)): dedupe, unlike
 * pruning, DOES remove `kind: 'part'` leaves, so an assembly whose only
 * member is a later duplicate becomes empty only after this pass runs — if
 * pruning ran first, that assembly's header would survive with zero rows
 * beneath it, which is exactly what D3(iii) forbids.
 */
/**
 * UX-spec §9.7 — first pass over the whole PRE-DEDUPE tree, indexing each
 * RESOLVED `partId` to the node ids that carry it, in tree (pre-order) order.
 *
 * Scoped to resolved leaves only, matching {@link dedupeByPartId}'s own domain
 * (D17(1)) — an unresolved leaf's `partId`, even if present, is never indexed
 * here, never drives another leaf's `occurrenceCount`, and is never a fan-out
 * target.
 *
 * ## Why this keeps the IDS and not just a count (Gate-5 C-D)
 *
 * This was `countResolvedPartIdOccurrences`, returning `Map<string, number>`.
 * `occurrenceCount` needs only the count, so the node ids — the one place where
 * a `partId`'s duplicate positions are still visible — were computed and
 * discarded a line later. Every consumer downstream sees the DEDUPED tree, which
 * holds exactly one node per `partId` by construction, so "act on all N
 * positions" was not implementable from anywhere outside this function. The
 * bulk delete removed one position of N and reported success as a direct
 * result.
 *
 * Keeping the ids makes both facts one derivation from one walk: the count is
 * now `ids.length`, so `occurrenceCount` and the fan-out cannot disagree about
 * how many positions a row stands for.
 */
function collectResolvedNodeIdsByPartId(
  nodes: readonly BomTreeNode[],
): Map<string, readonly string[]> {
  const byPartId = new Map<string, readonly string[]>()
  const walk = (list: readonly BomTreeNode[]): void => {
    for (const node of list) {
      if (node.kind === 'part') {
        if (node.partKind !== null && node.partId !== null) {
          byPartId.set(node.partId, [...(byPartId.get(node.partId) ?? []), node.nodeId])
        }
        continue
      }
      walk(node.children)
    }
  }
  walk(nodes)
  return byPartId
}

function dedupeByPartId(
  nodes: readonly BomTreeNode[],
  seenPartIds: Set<string>,
  /**
   * The pre-dedupe index from {@link collectResolvedNodeIdsByPartId}. The
   * surviving leaf's `occurrenceCount` is this array's LENGTH, so the number
   * the row discloses and the ids a row-scoped verb fans out to are the same
   * derivation — they cannot drift apart (Gate-5 C-D).
   */
  nodeIdsByPartId: ReadonlyMap<string, readonly string[]>,
): readonly BomTreeNode[] {
  const result: BomTreeNode[] = []
  for (const node of nodes) {
    if (node.kind === 'part') {
      // D17.1 — dedupe iff RESOLVED. `node.partKind !== null` already implies
      // a non-null `partId` (`:91-93`, `:103-104`), but `partId` is typed
      // `string | null` (`:40`), so the second clause is required for the
      // type-checker even though it is redundant at runtime. Keep BOTH
      // clauses — do not "tidy" this to a single test; the two-field form is
      // this module's convention (matches `collectLeafRefs` `:151` and
      // `collectSelectablePartIds` `:235`) and its absence here is exactly
      // what produced C3.
      if (node.partKind !== null && node.partId !== null) {
        if (seenPartIds.has(node.partId)) continue
        seenPartIds.add(node.partId)
        result.push({
          ...node,
          occurrenceCount: nodeIdsByPartId.get(node.partId)?.length ?? 1,
        })
        continue
      }
      result.push(node)
      continue
    }
    result.push({
      ...node,
      children: dedupeByPartId(node.children, seenPartIds, nodeIdsByPartId),
    })
  }
  return result
}

export interface UnifiedBomRows {
  /** The grouped, pruned tree — `bomHierarchy`'s ordering/grouping over the
   * population, per ADR-0142 D2. */
  readonly tree: readonly BomTreeNode[]
  /** ADR-0142 D2(4) / UX-spec §1.3 — every `identified[]`/`matched[]` part
   * whose `partId` is NOT reachable via any leaf in `tree`, appended as
   * flat, top-level, rail-less `BomTreeLeafNode`s, in `identified` then
   * `matched` order. This is the mechanism that carries ADR-0136 D2's
   * anti-BUG-1 protection forward once the buckets are gone: a coverage gap
   * changes WHERE a part renders, never WHETHER it renders. */
  readonly ungrouped: readonly BomTreeLeafNode[]
  /**
   * Gate-5 **C-D** — every RESOLVED `partId` in this session's hierarchy mapped
   * to the PRE-DEDUPE node ids that carry it, in tree order.
   *
   * `tree` is deduped, so it holds exactly one node per `partId` and cannot
   * answer "which BOM positions does this row stand for?". That question has a
   * real answer whenever `occurrenceCount > 1`, and every row-scoped write on
   * the catalog arm needs it: the row is the user's ONLY handle on all N
   * positions, because the dedupe means there is no second row to click. A verb
   * that reached only the surviving node half-acted on a BOM the user could not
   * see the rest of, and — the bulk delete's case — reported success for it.
   *
   * The first id is the surviving row's own `nodeId` (`dedupeByPartId` keeps the
   * first), so it agrees with the id the row's trash icon uses.
   *
   * ABSENT KEYS ARE MEANINGFUL, and callers must not paper over them. A
   * `partId` reachable only through `ungrouped` has no entry here at all,
   * because no hierarchy leaf references it — those leaves carry a CLIENT-ONLY
   * `nodeId` (`bom-ungrouped-*`) with no server row behind it. An empty/missing
   * lookup therefore means "no node-scoped verb can address this", which is a
   * refusal, not a reason to fall back to a `partId`-scoped verb.
   */
  readonly nodeIdsByPartId: ReadonlyMap<string, readonly string[]>
}

/**
 * ADR-0142 D2 — the single entry point for the unified "BOM parts" list.
 * NOT hierarchy-driven: population is `identified[] ∪ matched[]` (the same
 * population the two legacy buckets render); `bomHierarchy` supplies
 * ordering/grouping only, via `buildBomTree`. Any identified/matched part
 * that no hierarchy leaf references is NOT dropped (that would be ADR-0086
 * BUG-1, arriving through the data-flow direction — see the ADR's D2) — it
 * is appended to `ungrouped`, still selectable, still counted.
 *
 * Order of operations (CORRECTED at Gate-5, ADR-0142 D14.1(2) — the previous
 * text here was wrong): **build → dedupe → prune → coverage-diff.** The old
 * comment justified prune-then-diff on the ground that pruning never removes
 * a `kind: 'part'` leaf — true of pruning alone, but {@link dedupeByPartId}
 * DOES remove part leaves, so dedupe must run first: an assembly whose only
 * member is a later duplicate becomes empty only once dedupe has run, and
 * pruning must see that emptied state to drop the header (D3(iii) forbids a
 * header surviving with zero rows beneath it). The coverage diff then runs
 * against the POST-dedupe, POST-prune referenced set, so a deduped-away
 * leaf's `partId` — still referenced by the surviving occurrence — is never
 * also appended to `ungrouped`.
 */
export function buildUnifiedBomRows(
  bomHierarchy: readonly StagingBomNode[],
  identified: readonly IdentifiedPart[],
  matched: readonly MatchedPart[],
): UnifiedBomRows {
  // UX-spec §1.1/§1.3 — the coverage diff is scoped to an actual BOM
  // session (a hierarchy was genuinely captured). An ABSENT/empty
  // `bomHierarchy` is not "a BOM session whose coverage happens to be
  // zero" — it is a non-BOM session, and §1.1 is explicit that nothing in
  // this document touches it. Diffing against an empty forest would put
  // EVERY identified/matched part into `ungrouped` and wrongly flip
  // `hasBomRows` true for the dominant, permanent CAD-only case — exactly
  // the D4/§1.1 "wholly unaffected" guarantee this early return protects.
  if (bomHierarchy.length === 0) return { tree: [], ungrouped: [], nodeIdsByPartId: new Map() }

  const rawTree = buildBomTree(bomHierarchy, identified, matched)
  // Indexed BEFORE the dedupe, which is the only moment the duplicate positions
  // are still visible — and returned, not just consumed, per C-D.
  const nodeIdsByPartId = collectResolvedNodeIdsByPartId(rawTree)
  const dedupedTree = dedupeByPartId(rawTree, new Set(), nodeIdsByPartId)
  const tree = pruneEmptyAssemblies(dedupedTree)
  const referencedPartIds = new Set(tree.flatMap((node) => collectLeafPartIds(node)))

  const ungrouped: BomTreeLeafNode[] = []
  // Gate-5 re-review residual — `ungrouped` is its own flat sibling array
  // (never a wire-tree parent), so `siblingIndex` here is this array's own
  // position, tracked across BOTH loops below (identified then matched,
  // §D2(4)'s own append order) rather than restarting at 0 for `matched` —
  // restarting would let an identified and a matched ungrouped leaf collide
  // on the same `siblingIndex`.
  let ungroupedSiblingIndex = 0
  for (const part of identified) {
    if (referencedPartIds.has(part.partId)) continue
    ungrouped.push({
      kind: 'part',
      nodeId: `bom-ungrouped-identified-${part.partId}`,
      name: part.name,
      bomLine: null,
      quantity: part.quantity,
      // No wire node backs this leaf (never read on the identified arm
      // anyway — D1.1), and no BOM position to have a working value either.
      desiredQuantity: null,
      partId: part.partId,
      // ADR-0149 Am.1 D17 — no wire `StagingBomNode` backs a coverage-diff
      // leaf (it's synthesized from the flat array, never a tree position),
      // so there is no line-level assertion to carry. `null`, not fabricated.
      statedPartType: null,
      statedMaterial: null,
      // UID-517 item 4 — no wire node, so no cross-session decoration either.
      crossSessionThumbnailUrl: null,
      statedProcess: null,
      // UID-497 — same reasoning as the two stated values above: no wire node
      // backs a coverage-diff leaf, so there is no per-position drawing or
      // material choice to carry. The identified/matched arms' own drawing
      // lives on the PART (`IdentifiedPart.drawingFilename`) and is rendered
      // from there, which is why `null` here loses nothing.
      selectedMaterial: null,
      drawingFilename: null,
      occurrenceCount: 1,
      siblingIndex: ungroupedSiblingIndex++,
      // ADR-0157 D3 — no wire node, no dismissal concept (same reasoning as
      // `statedPartType` above).
      dismissedAt: null,
      partKind: 'identified',
      part,
    })
  }
  for (const part of matched) {
    if (referencedPartIds.has(part.partId)) continue
    ungrouped.push({
      kind: 'part',
      nodeId: `bom-ungrouped-matched-${part.partId}`,
      name: part.name,
      bomLine: null,
      quantity: part.quantity ?? null,
      // ADR-0156 R1/finding — no wire `StagingBomNode` backs a coverage-diff
      // leaf, so there is no real node to hold an edit. The synthesized
      // `nodeId` above is a CLIENT-ONLY id with no server-side row; see the
      // frontend-engineer's report for this residual gap.
      desiredQuantity: null,
      partId: part.partId,
      // ADR-0149 Am.1 D17 — same reasoning as the identified branch above.
      statedPartType: null,
      statedMaterial: null,
      // UID-517 item 4 — no wire node, so no cross-session decoration either.
      crossSessionThumbnailUrl: null,
      statedProcess: null,
      // UID-497 — same reasoning as the two stated values above: no wire node
      // backs a coverage-diff leaf, so there is no per-position drawing or
      // material choice to carry. The identified/matched arms' own drawing
      // lives on the PART (`IdentifiedPart.drawingFilename`) and is rendered
      // from there, which is why `null` here loses nothing.
      selectedMaterial: null,
      drawingFilename: null,
      occurrenceCount: 1,
      siblingIndex: ungroupedSiblingIndex++,
      // ADR-0157 D3 — same reasoning as the identified branch above.
      dismissedAt: null,
      partKind: 'matched',
      part,
    })
  }

  return { tree, ungrouped, nodeIdsByPartId }
}

export type CheckState = 'checked' | 'unchecked' | 'mixed'

/**
 * Derive a group/header checkbox's tri-state value from its scope's leaf
 * `partId`s and the current selection `Set`. An empty scope (no selectable
 * leaves) is "unchecked", never "checked" (nothing to vacuously satisfy) —
 * this is a design choice, not a formalism claim (vacuous truth would say
 * "checked"; a UI checkbox with no children under it means the user hasn't
 * selected anything visible, so unchecked reads better than checked).
 */
export function computeCheckState(
  leafPartIds: readonly string[],
  selected: ReadonlySet<string>,
): CheckState {
  if (leafPartIds.length === 0) return 'unchecked'
  const selectedCount = leafPartIds.filter((id) => selected.has(id)).length
  if (selectedCount === 0) return 'unchecked'
  if (selectedCount === leafPartIds.length) return 'checked'
  return 'mixed'
}

/**
 * ADR-0157 — every direct-or-nested `kind: 'part'` LEAF row under a node
 * (mirrors {@link countRenderableRows}'s "renders a row" predicate, resolved
 * and unresolved alike) alongside how many of them are dismissed
 * (`dismissedAt !== null`). Used by `formatAssemblySubLabel`
 * (`StagingBomPartsTree.tsx`) to append "All N lines dismissed" ONLY when
 * every leaf in the subtree is both present and dismissed — an assembly with
 * zero leaves (already pruned by {@link pruneEmptyAssemblies} before this
 * would ever run) must never read as "all dismissed" (vacuously true is the
 * wrong answer here, unlike `computeCheckState`'s empty-scope case: an empty
 * assembly is pruned away entirely, so this function is never actually
 * called with zero leaves in practice, but the guard is explicit rather than
 * relying on that invariant silently).
 */
export function countDismissedLeaves(node: BomTreeNode): { total: number; dismissed: number } {
  if (node.kind === 'part') {
    return { total: 1, dismissed: node.dismissedAt !== null ? 1 : 0 }
  }
  return node.children.reduce(
    (acc, child) => {
      const { total, dismissed } = countDismissedLeaves(child)
      return { total: acc.total + total, dismissed: acc.dismissed + dismissed }
    },
    { total: 0, dismissed: 0 },
  )
}

/**
 * ADR-0157 — focus-management fallback after a leaf row's own Remove control
 * disappears from the DOM (the row itself is dropped from the tree — this
 * never applies to Dismiss, which keeps its row in place and swaps the
 * button to Undo instead, so focus never leaves the control it started on).
 *
 * Ordered per the human's ruling: the NEXT removable leaf sibling (same
 * immediate scope — an assembly's own children, or the top-level/`ungrouped`
 * scope for a loose leaf) → else the PREVIOUS removable leaf sibling → else
 * the parent assembly's own checkbox → else the section heading (the caller
 * owns focusing that fallback target; this function only names it).
 *
 * "Removable" here means a leaf that renders a Remove control at all
 * (`partKind !== null` — identified, matched, and catalog all qualify post
 * ADR-0157 D1; an unresolved leaf never has a Remove, only Dismiss, and is
 * excluded from this sibling scan for that reason).
 */
export type FocusFallbackTarget =
  | { readonly kind: 'leaf-remove'; readonly nodeId: string }
  | { readonly kind: 'assembly-checkbox'; readonly nodeId: string }
  | { readonly kind: 'section-heading' }

function collectRemovableSiblingIds(nodes: readonly BomTreeNode[]): readonly string[] {
  const ids: string[] = []
  for (const node of nodes) {
    if (node.kind === 'part' && node.partKind !== null) ids.push(node.nodeId)
  }
  return ids
}

interface RemovalContext {
  readonly siblingIds: readonly string[]
  readonly parentAssemblyNodeId: string | null
}

function findRemovalContext(
  nodes: readonly BomTreeNode[],
  targetNodeId: string,
  parentAssemblyNodeId: string | null,
): RemovalContext | null {
  const siblingIds = collectRemovableSiblingIds(nodes)
  if (siblingIds.includes(targetNodeId)) {
    return { siblingIds, parentAssemblyNodeId }
  }
  for (const node of nodes) {
    if (node.kind === 'assembly') {
      const found = findRemovalContext(node.children, targetNodeId, node.nodeId)
      if (found) return found
    }
  }
  return null
}

export function findRemovalFocusFallback(
  tree: readonly BomTreeNode[],
  ungrouped: readonly BomTreeLeafNode[],
  removedNodeId: string,
): FocusFallbackTarget {
  const treeContext = findRemovalContext(tree, removedNodeId, null)
  const ungroupedSiblingIds = collectRemovableSiblingIds(ungrouped)
  const context =
    treeContext ??
    (ungroupedSiblingIds.includes(removedNodeId)
      ? { siblingIds: ungroupedSiblingIds, parentAssemblyNodeId: null }
      : null)
  if (!context) return { kind: 'section-heading' }
  const { siblingIds, parentAssemblyNodeId } = context
  const index = siblingIds.indexOf(removedNodeId)
  const next = siblingIds[index + 1]
  if (next !== undefined) return { kind: 'leaf-remove', nodeId: next }
  const previous = siblingIds[index - 1]
  if (previous !== undefined) return { kind: 'leaf-remove', nodeId: previous }
  if (parentAssemblyNodeId !== null) {
    return { kind: 'assembly-checkbox', nodeId: parentAssemblyNodeId }
  }
  return { kind: 'section-heading' }
}

/**
 * EVERY BOM `nodeId` whose leaf carries `partId` — empty when none does.
 *
 * ## Why this exists — Gate-5 C4 (UID-496)
 *
 * The row checkbox yields `partId`s (`collectSelectablePartIds`), and the only
 * consumer of a selection is the bulk delete, which calls the `partId`-scoped
 * `DELETE /staging/parts/:partId`. That verb searches the PERSISTED triage,
 * where a catalog leaf does not exist — it has no `intake.staged_file` row by
 * construction (migration 1013's arm mutex). So selecting a catalog row and
 * pressing "Delete selected" produced *"We couldn't remove 1 of the 1 selected
 * parts"* and left the row in place, while the CORRECT verb (`removeBomNode`,
 * node-scoped) was reachable only from that row's own trash icon.
 *
 * Opening the checkbox on catalog leaves is what made that path reachable
 * on-screen, so the bulk delete now needs the same `partId -> nodeId`
 * resolution the row itself already has. **This is a FOURTH walk, and
 * deliberately so** — the module header explains why `collectLeafRefs`,
 * `collectSelectablePartIds` and the render walk are independent rather than
 * re-derived from one another, and the same reasoning applies here: this one
 * answers "which node do I address to delete this part?", which is a different
 * question from "may this part be selected?".
 *
 * ## Gate-5 C-3 — ALL of them, and the TREE ONLY
 *
 * The first version returned the FIRST match and also fell back to the
 * `ungrouped` leaves. Both were wrong:
 *
 *  - **First match only.** `dropBomNode` removes ONE node, so a bulk delete of
 *    a row standing for N BOM positions removed one and left the row on
 *    screen — with NO error, because the one call succeeded. That traded the
 *    old reported failure for a silent no-op, which is worse. The row's own
 *    trash icon does not have this problem: `handleRemoveCatalogNode` takes
 *    `occurrenceCount` and announces "all N positions".
 *  - **The `ungrouped` fallback.** Those leaves carry a CLIENT-ONLY id
 *    (`bom-ungrouped-matched-${partId}`) with no server-side row — this
 *    module's own comment says so — so passing it to `removeBomNode` is a
 *    guaranteed 404. Returning nothing is the honest answer, and the caller
 *    now treats empty as a refusal rather than falling through to a verb it
 *    has already proven cannot work (Gate-5 C-4).
 *
 * Tree order, so the first element is the node the deduped row itself carries
 * (`dedupeByPartId` keeps the first) — the same id the trash icon uses.
 *
 * ## THE LIMITATION THAT USED TO BE HERE IS FIXED (Gate-5 C-D)
 *
 * This took `tree` and WALKED it. That could never return more than one id:
 * `tree` is the DEDUPED tree, so it holds exactly one node per `partId` by
 * construction, and the "EVERY" in the contract above was unreachable — the
 * signature promised a fan-out the available data could not supply. The bulk
 * delete looked like it removed all N positions and removed one.
 *
 * It now reads {@link UnifiedBomRows.nodeIdsByPartId}, the index built from the
 * PRE-dedupe tree, so the contract is real. Takes the whole `UnifiedBomRows`
 * rather than the map alone so a caller cannot accidentally pass the deduped
 * tree again and silently get the old behaviour back.
 *
 * Callers MUST pass the UNFILTERED rows. Search-filtering is a rendering
 * concern; a `partId` scrolled out of view still has its other BOM positions,
 * and resolving against filtered rows would make a delete's blast radius depend
 * on the text in the search box. `filterUnifiedBomRows` carries this index
 * through unchanged for exactly that reason.
 */
export function findNodeIdsForPartId(rows: UnifiedBomRows, partId: string): readonly string[] {
  return rows.nodeIdsByPartId.get(partId) ?? []
}
